import React, { useImperativeHandle, forwardRef, useState } from 'react'
import { Input, Popover, Progress, Space, Typography } from 'antd'
import { red, yellow, green, blue } from '@ant-design/colors'
import { CloseCircleOutlined, CheckCircleFilled } from '@ant-design/icons'
import ProForm from '@ant-design/pro-form'
import { useControllableValue } from 'ahooks'
import { isUndefined, isBoolean } from 'lodash'
import { motion, AnimatePresence } from 'framer-motion'
import { checkPassword } from '@views/Login/api'
import { useValidator } from './validator'
import styles from './styles.less'

const { Text } = Typography

function Toggle({ visible, children, height }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, overflow: 'hidden' }}
          animate={{ height }}
          exit={{
            height: 0,
            overflow: 'hidden',
            transition: { delay: 0.2 }
          }}
          transition={{ ease: [0.08, 0.82, 0.17, 1] }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * @param {number} score
 */
function getLevel(score) {
  if (score > 80) return 'high'
  else if (score > 50) return 'middle'
  return 'low'
}

const levelInfoMap = {
  high: '高',
  middle: '中',
  low: '低'
}

const levelColorMap = {
  high: green,
  middle: yellow,
  low: red
}

const rulesText = [
  '输入至少8个字符',
  '不能包含4个及以上的重复字符',
  '不能使用标准键盘上的相邻字符',
  '不能使用常用单词及其逆序和变体',
  '不能使用历史密码'
]

function RuleList({ actives }) {
  return (
    <Space direction="vertical">
      {rulesText.map((text, index) => (
        <Space key={index}>
          {actives[index] ? (
            <CheckCircleFilled style={{ color: blue.primary }} />
          ) : (
            <CloseCircleOutlined style={{ color: red.primary }} />
          )}
          <Text type={actives[index] ? 'secondary' : 'danger'}>{text}</Text>
        </Space>
      ))}
    </Space>
  )
}

export function Password(
  { showLevel = false, showRules = false, rules = [], loading, ...props },
  ref
) {
  const [value, _onChange] = useControllableValue(props)
  const validator = useValidator()

  useImperativeHandle(ref, () => ({ validator: validator }))

  const onChange = e => {
    validator.set(e?.target?.value)
    _onChange(e)
  }

  const score = validator.getScore?.()
  const level = getLevel(score)
  let visible = isUndefined(showRules) || showRules
  if (value === '' || isUndefined(value)) visible = false

  return (
    <Space direction="vertical" className="full-width">
      <Popover
        content={<RuleList actives={rules} />}
        placement="right"
        visible={visible}
      >
        <Input.Password
          {...props}
          {...{
            value,
            onChange
          }}
        />
      </Popover>
      <Toggle visible={score && showLevel} height={22}>
        <Progress
          className={styles.level}
          percent={score / 0.95}
          strokeColor={levelColorMap[level].primary}
          format={_ => '强度' + levelInfoMap[level]}
        />
      </Toggle>
    </Space>
  )
}

const PasswordWrapper = forwardRef(Password)

const isFalse = v => isBoolean(v) && !v
PasswordWrapper.ProField = function ProFieldPassword({
  fieldProps = {},
  ...props
}) {
  const [rules, setRules] = useState([])

  const formValidator = async (_, value) => {
    if (value === '' || typeof value === 'undefined') return Promise.resolve()
    const result = await checkPassword({
      newPwd: value
    })
    if (result?.length) {
      return Promise.reject(
        result.map(i => (
          <Typography.Paragraph type="danger" key={i}>
            {i}
          </Typography.Paragraph>
        ))
      )
      // return Promise.reject(result.join('。\n'))
    }
    return Promise.resolve()
  }

  return (
    <ProForm.Item
      {...props}
      className={styles.formItem}
      hasFeedback
      validateTrigger={['onChange', 'onBlur']}
      rules={[
        ...props.rules,
        isFalse(fieldProps.showRules) || {
          validator: formValidator,
          validateTrigger: 'onBlur'
        }
      ].filter(Boolean)}
    >
      <PasswordWrapper
        showRules={false}
        showLevel
        rules={rules}
        autoComplete="new-password"
        {...fieldProps}
      />
    </ProForm.Item>
  )
}
export default PasswordWrapper
